import React from 'react';
import { BookOpen, Upload, FileText, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

/**
 * StyleDocumentsCard - Gestion des documents de style d'écriture
 */
const StyleDocumentsCard = ({
  books,
  loading,
  showUpload,
  setShowUpload,
  bookTitle,
  setBookTitle,
  selectedFile,
  uploading,
  getRootProps,
  getInputProps,
  isDragActive,
  onUpload,
  onDelete,
}) => {
  return (
    <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-xl md:text-2xl font-orbitron uppercase tracking-wider text-cyan-400 flex items-center">
          <BookOpen className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
          Documents de style
        </CardTitle>
        
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogTrigger asChild>
            <Button
              data-testid="btn-add-book"
              className="bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border-2 border-cyan-500/50 hover:border-cyan-400 rounded-lg px-4 py-2 font-semibold uppercase tracking-wider text-sm"
            >
              <Upload className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-950 border-cyan-500/50 max-w-md mx-4">
            <DialogHeader>
              <DialogTitle className="text-xl font-orbitron text-cyan-400">Ajouter un document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-slate-400 uppercase tracking-wider font-semibold">Titre</label>
                <Input
                  data-testid="input-book-title"
                  value={bookTitle}
                  onChange={(e) => setBookTitle(e.target.value)}
                  placeholder="Ex: Mon livre, Article de référence..."
                  className="mt-2 bg-slate-950/50 border-2 border-slate-700 text-slate-100 rounded-lg"
                />
              </div>

              <div
                {...getRootProps()}
                data-testid="dropzone-book"
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                  isDragActive
                    ? 'border-cyan-400 bg-cyan-500/10'
                    : 'border-slate-700 hover:border-cyan-500/50 bg-slate-900/50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="w-10 h-10 mx-auto mb-3 text-cyan-400" />
                {selectedFile ? (
                  <p className="text-cyan-400 text-sm font-medium break-words">{selectedFile.name}</p>
                ) : (
                  <div>
                    <p className="text-slate-300 text-sm mb-1 font-medium">Glissez un fichier ici</p>
                    <p className="text-xs text-slate-500">PDF, Word ou TXT</p>
                  </div>
                )}
              </div>

              <Button
                onClick={onUpload}
                disabled={uploading || !bookTitle || !selectedFile}
                data-testid="btn-upload-book"
                className="w-full bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border-2 border-cyan-500/50 hover:border-cyan-400 rounded-lg py-3 font-semibold uppercase tracking-wider"
              >
                {uploading ? 'Upload en cours...' : 'Ajouter le document'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      
      <CardContent>
        <p className="text-sm text-slate-500 mb-4">
          Ces documents servent de référence pour que l&apos;IA imite votre style d&apos;écriture.
        </p>
        
        {loading ? (
          <p className="text-slate-400 text-center py-8">Chargement...</p>
        ) : books.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-slate-800 rounded-lg">
            <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400 font-medium">Aucun document ajouté</p>
            <p className="text-slate-500 text-sm mt-1">Ajoutez vos livres ou articles pour définir votre style</p>
          </div>
        ) : (
          <div className="space-y-3">
            {books.map((book) => (
              <div
                key={book.id}
                data-testid={`book-card-${book.id}`}
                className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-800 hover:border-cyan-500/30 transition-colors"
              >
                <div className="flex items-center min-w-0 flex-1">
                  <FileText className="w-5 h-5 mr-3 text-purple-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-slate-200 font-medium truncate">{book.title}</p>
                    <p className="text-xs text-slate-500 truncate">{book.file_name}</p>
                  </div>
                </div>
                <button
                  onClick={() => onDelete(book.id)}
                  data-testid={`btn-delete-book-${book.id}`}
                  className="text-slate-500 hover:text-red-400 transition-colors ml-3 p-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StyleDocumentsCard;
